import { useCallback, useEffect, useState } from "react";
import { Activity, Undo2, RefreshCw, Mail, Link2, MessageSquare, Send, ShoppingCart, AlertTriangle, Inbox, Bot } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { api, errMsg } from "../lib/api";
import { fmtDateTime } from "../lib/format";

const ICONS = {
  mail_imported: Mail, auto_link: Link2, link: Link2, question: MessageSquare, missing_sent: Send, digest: Send, ask: MessageSquare,
  amazon: ShoppingCart, error: AlertTriangle, inbox: Inbox, undo: Undo2, monitor: Mail, reset: RefreshCw,
};
const LABELS = {
  mail_repair: "Mail-PDF-Reparatur", mail_imported: "Mail importiert", auto_link: "Automatisch zugeordnet", link: "Zugeordnet", question: "Frage beantwortet", missing_sent: "Nachricht gesendet",
  digest: "Tagesübersicht", ask: "Rückfrage", amazon: "Amazon-Bestellung", error: "Fehler", inbox: "Belege-Eingang", undo: "Rückgängig",
  monitor: "Postfach", receipt_received: "Beleg erhalten", status: "Status", paired: "Kopplung", rejected: "Abgelehnt", webhook: "Webhook",
  bot: "Bot", new_transactions: "Neue Buchungen", reset: "Zurückgesetzt", maintenance: "Wartung",
};

const SchedulerStatus = ({ s }) => {
  const sc = s?.scheduler || {};
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 text-xs text-slate-500" data-testid="scheduler-status">
      Telegram: {s?.paired ? `gekoppelt (${s.chat_name})` : "nicht gekoppelt"} · Scheduler aktiv seit {sc.last_tick ? fmtDateTime(sc.last_tick) : "–"}
    </div>
  );
};

const OldMailData = ({ onDone }) => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.mailDataStatus().then(setSt).catch(() => {}); }, []);
  if (!st?.total) return null;
  const c = st.counts;
  const del = async () => {
    if (!window.confirm("Alte Postfach-Daten (Zugangsdaten, Mail-Treffer, Jobs, Amazon-Hinweise) endgültig löschen? Belege bleiben erhalten.")) return;
    setBusy(true);
    try { await api.mailDataDelete(); toast.success("Alte Postfach-Daten gelöscht"); setSt(await api.mailDataStatus()); onDone?.(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm" data-testid="old-mail-data-card">
      <div className="text-slate-700"><b>Alte Postfach-Daten</b> (Postfach-Suche wurde entfernt)
        <div className="text-xs text-slate-600" data-testid="old-mail-data-counts">{c.mail_accounts} Postfach-Zugänge, {c.mail_matches} Mail-Treffer, {c.mail_messages} Mail-Cache, {c.mail_jobs + c.import_jobs} Jobs, {c.amazon_orders} Amazon-Hinweise · Belege bleiben erhalten</div>
      </div>
      <Button variant="outline" onClick={del} disabled={busy} className="h-9 gap-1.5 border-red-200 text-red-700 hover:bg-red-50" data-testid="old-mail-data-delete-btn">Jetzt löschen</Button>
    </div>
  );
};

const Entry = ({ a, onUndo }) => {
  const Icon = ICONS[a.type] || Bot;
  return (
    <li className="flex items-start gap-3 border-b border-slate-100 px-4 py-3 last:border-0" data-testid={`activity-row-${a.id}`}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <span className="font-num">{fmtDateTime(a.at)}</span>
          <span className="rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-700">{LABELS[a.type] || a.type}</span>
          {a.undone && <span className="rounded bg-amber-100 px-1.5 py-0.5 font-medium text-amber-800">rückgängig gemacht</span>}
        </div>
        <div className={`mt-0.5 break-words text-sm [overflow-wrap:anywhere] ${a.undone ? "text-slate-400 line-through" : "text-slate-900"}`} data-testid={`activity-text-${a.id}`}>{a.text}</div>
        {a.answer && <div className="mt-1 whitespace-pre-wrap rounded bg-slate-50 p-2 text-xs text-slate-600">{a.answer}</div>}
      </div>
      {a.can_undo && <Button size="sm" variant="outline" onClick={() => onUndo(a)} className="gap-1" data-testid={`activity-undo-${a.id}`}><Undo2 className="h-3.5 w-3.5" />Rückgängig</Button>}
    </li>
  );
};

export default function AgentActivity({ onDataChanged }) {
  const [items, setItems] = useState([]);
  const [s, setS] = useState(null);
  const load = useCallback(() => Promise.all([api.agentActivity().then(setItems), api.agentSettings().then(setS)]), []);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);
  const undo = async (a) => {
    try { const r = await api.agentUndo(a.id); toast.success(r.message); load(); onDataChanged?.(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid min-w-0 max-w-4xl gap-5 p-4 sm:p-6 lg:p-8 fade-up [&>*]:min-w-0" data-testid="agent-activity-view">
      <div className="flex items-end justify-between gap-3">
        <h1 className="flex items-center gap-2 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl"><Activity className="h-6 w-6" />Agent-Aktivität</h1>
        <Button variant="ghost" onClick={load} className="gap-1.5" data-testid="activity-refresh-btn"><RefreshCw className="h-4 w-4" />Aktualisieren</Button>
      </div>
      <SchedulerStatus s={s} />
      <OldMailData onDone={load} />
      {items.length === 0 ? (
        <p className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500" data-testid="activity-empty">Noch keine Aktivität. Sobald der Agent Belege zuordnet oder Nachrichten schickt, steht es hier.</p>
      ) : (
        <ul className="rounded-xl border border-slate-200 bg-white" data-testid="activity-list">{items.map((a) => <Entry key={a.id} a={a} onUndo={undo} />)}</ul>
      )}
    </div>
  );
}
